/**
 * Thin fetch wrapper around the Salsa Shop Store OS API.
 *
 * The API base URL is configurable through `VITE_API_BASE_URL` and defaults to
 * the compose-published backend on port 8000.
 */
import type { DashboardPayload, SystemHealth } from '../types/api'

export const API_BASE_URL: string = (
  import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:8000'
).replace(/\/+$/, '')

export const HEALTH_ENDPOINT = '/api/v1/health'
export const DASHBOARD_ENDPOINT = '/api/v1/mock/dashboard'

export class ApiError extends Error {
  readonly status: number
  readonly url: string

  constructor(message: string, status: number, url: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.url = url
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const url = `${API_BASE_URL}${path}`

  let response: Response
  try {
    response = await fetch(url, {
      headers: { Accept: 'application/json', ...(init?.headers ?? {}) },
      ...init,
    })
  } catch {
    throw new ApiError(
      `Cannot reach the Store OS API at ${url}. Is the backend container running?`,
      0,
      url,
    )
  }

  if (!response.ok) {
    throw new ApiError(`Request to ${path} failed with HTTP ${response.status}`, response.status, url)
  }

  return (await response.json()) as T
}

/**
 * Health probe.
 *
 * A degraded backend answers with HTTP 503 but still returns the full JSON body;
 * that payload is needed to render per-dependency status, so it is parsed here
 * instead of being treated as a failure.
 */
export async function fetchHealth(): Promise<SystemHealth> {
  const url = `${API_BASE_URL}${HEALTH_ENDPOINT}`

  let response: Response
  try {
    response = await fetch(url, { headers: { Accept: 'application/json' } })
  } catch {
    throw new ApiError(`Cannot reach the Store OS API at ${url}`, 0, url)
  }

  const payload = (await response.json()) as SystemHealth

  if (!response.ok && payload?.status !== 'degraded') {
    throw new ApiError(`Health endpoint returned HTTP ${response.status}`, response.status, url)
  }

  return payload
}

export function fetchDashboard(): Promise<DashboardPayload> {
  return request<DashboardPayload>(DASHBOARD_ENDPOINT)
}
